import { useFrame } from '@react-three/fiber'
import { useCallback, useLayoutEffect, useMemo, useRef } from 'react'
import * as THREE from 'three'
import type { GameRuntime } from '../game/runtime'
import { getTrackSample } from '../game/track'
import type { Track as TrackState } from '../game/types'
import { createCheckpointMarkers } from './checkpoint-marker'
import { createSkylineBuildings } from './skyline'
import { createTrackRailSegments, decodeRailBreak } from './track-rail'

interface TrackProps {
  track: TrackState
  runtime?: GameRuntime
}

const START_LINE_TILE_IDS = ['a', 'b', 'c', 'd', 'e', 'f', 'g', 'h', 'i', 'j', 'k', 'l'] as const
const BRAKE_STRIPE_OFFSETS = [-0.34, 0, 0.34] as const

function buildRoadGeometry(track: TrackState): THREE.BufferGeometry {
  const positions: number[] = []
  const uvs: number[] = []
  const indices: number[] = []
  for (const sample of track.samples) {
    const leftX = sample.position.x + sample.normal.x * track.width
    const leftZ = sample.position.y + sample.normal.y * track.width
    const rightX = sample.position.x - sample.normal.x * track.width
    const rightZ = sample.position.y - sample.normal.y * track.width
    positions.push(leftX, 0, leftZ, rightX, 0, rightZ)
    const v = sample.distance / 14
    uvs.push(0, v, 1, v)
  }
  for (let index = 0; index < track.samples.length; index += 1) {
    const next = (index + 1) % track.samples.length
    const left = index * 2
    const right = left + 1
    const nextLeft = next * 2
    const nextRight = nextLeft + 1
    indices.push(left, right, nextLeft, right, nextRight, nextLeft)
  }
  const geometry = new THREE.BufferGeometry()
  geometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3))
  geometry.setAttribute('uv', new THREE.Float32BufferAttribute(uvs, 2))
  geometry.setIndex(indices)
  geometry.computeVertexNormals()
  return geometry
}

function Curbs({ track }: TrackProps) {
  const samples = useMemo(() => track.samples.filter((_, index) => index % 2 === 0), [track])
  const ref = useRef<THREE.InstancedMesh>(null)
  useLayoutEffect(() => {
    const mesh = ref.current
    if (!mesh) return
    const dummy = new THREE.Object3D()
    const cyan = new THREE.Color('#3cecff')
    const magenta = new THREE.Color('#ff315f')
    samples.forEach((sample, index) => {
      for (let side = -1; side <= 1; side += 2) {
        const instance = index * 2 + (side === 1 ? 1 : 0)
        dummy.position.set(
          sample.position.x + sample.normal.x * (track.width + 0.55) * side,
          0.22,
          sample.position.y + sample.normal.y * (track.width + 0.55) * side,
        )
        dummy.rotation.y = Math.atan2(sample.tangent.x, sample.tangent.y)
        dummy.scale.set(1.15, 0.22, 2.1)
        dummy.updateMatrix()
        mesh.setMatrixAt(instance, dummy.matrix)
        mesh.setColorAt(instance, (index + side) % 4 < 2 ? cyan : magenta)
      }
    })
    mesh.instanceMatrix.needsUpdate = true
    if (mesh.instanceColor) mesh.instanceColor.needsUpdate = true
  }, [samples, track.width])
  return (
    <instancedMesh ref={ref} args={[undefined, undefined, samples.length * 2]} castShadow receiveShadow>
      <boxGeometry />
      <meshStandardMaterial roughness={0.3} metalness={0.35} emissive="#172d38" emissiveIntensity={1.8} />
    </instancedMesh>
  )
}

function BarrierRails({ track, runtime }: TrackProps) {
  const rails = useMemo(() => createTrackRailSegments(track), [track])
  const bodyRef = useRef<THREE.InstancedMesh>(null)
  const glowRef = useRef<THREE.InstancedMesh>(null)
  const brokenUntilRef = useRef(new Map<string, number>())
  const lastEventIdRef = useRef(0)
  const dummy = useMemo(() => new THREE.Object3D(), [])

  const updateRails = useCallback(
    (time: number) => {
      const body = bodyRef.current
      const glow = glowRef.current
      if (!body || !glow) return

      rails.forEach((rail, index) => {
        const visible = (brokenUntilRef.current.get(rail.key) ?? 0) <= time
        dummy.position.set(rail.position.x, rail.height / 2 + 0.08, rail.position.y)
        dummy.rotation.set(0, rail.rotationY, 0)
        dummy.scale.set(visible ? 0.34 : 0, visible ? rail.height : 0, visible ? rail.length + 0.12 : 0)
        dummy.updateMatrix()
        body.setMatrixAt(index, dummy.matrix)

        dummy.position.y = rail.height + 0.11
        dummy.scale.set(visible ? 0.12 : 0, visible ? 0.07 : 0, visible ? rail.length + 0.16 : 0)
        dummy.updateMatrix()
        glow.setMatrixAt(index, dummy.matrix)
        glow.setColorAt(index, new THREE.Color(rail.accent))
      })

      body.instanceMatrix.needsUpdate = true
      glow.instanceMatrix.needsUpdate = true
      if (glow.instanceColor) glow.instanceColor.needsUpdate = true
    },
    [dummy, rails],
  )

  useLayoutEffect(() => updateRails(0), [updateRails])
  useFrame(() => {
    if (!runtime) return
    const state = runtime.getState()
    let changed = false
    if (state.nextEventId <= lastEventIdRef.current) {
      lastEventIdRef.current = 0
      brokenUntilRef.current.clear()
      changed = true
    }
    for (const event of state.events) {
      if (event.id <= lastEventIdRef.current || event.type !== 'rail-break') continue
      const broken = decodeRailBreak(event.value)
      if (!broken) continue
      for (let offset = -2; offset <= 2; offset += 1) {
        const sampleIndex = (broken.sampleIndex + offset + track.samples.length) % track.samples.length
        brokenUntilRef.current.set(`${sampleIndex}:${broken.side}`, state.time + 3.4)
      }
      changed = true
    }
    if (state.events.length > 0) {
      lastEventIdRef.current = Math.max(lastEventIdRef.current, ...state.events.map((event) => event.id))
    }
    for (const [key, expiry] of brokenUntilRef.current) {
      if (expiry > state.time) continue
      brokenUntilRef.current.delete(key)
      changed = true
    }
    if (changed) updateRails(state.time)
  })

  return (
    <group>
      <instancedMesh ref={bodyRef} args={[undefined, undefined, rails.length]} castShadow receiveShadow>
        <boxGeometry />
        <meshStandardMaterial color="#0b111b" metalness={0.88} roughness={0.3} />
      </instancedMesh>
      <instancedMesh ref={glowRef} args={[undefined, undefined, rails.length]}>
        <boxGeometry />
        <meshBasicMaterial color="#ffffff" toneMapped={false} />
      </instancedMesh>
    </group>
  )
}

function CenterGuides({ track }: TrackProps) {
  const samples = useMemo(
    () =>
      track.samples.filter((sample, index) => {
        if (index % 5 !== 0) return false
        const future = getTrackSample(track, index + 5)
        const alignment = THREE.MathUtils.clamp(
          sample.tangent.x * future.tangent.x + sample.tangent.y * future.tangent.y,
          -1,
          1,
        )
        return Math.acos(alignment) < 0.08
      }),
    [track],
  )
  const ref = useRef<THREE.InstancedMesh>(null)
  useLayoutEffect(() => {
    const mesh = ref.current
    if (!mesh) return
    const dummy = new THREE.Object3D()
    samples.forEach((sample, index) => {
      dummy.position.set(sample.position.x, 0.13, sample.position.y)
      dummy.rotation.y = Math.atan2(sample.tangent.x, sample.tangent.y)
      dummy.scale.set(0.13, 0.025, 2.6)
      dummy.updateMatrix()
      mesh.setMatrixAt(index, dummy.matrix)
    })
    mesh.instanceMatrix.needsUpdate = true
  }, [samples])
  return (
    <instancedMesh ref={ref} args={[undefined, undefined, samples.length]} receiveShadow>
      <boxGeometry />
      <meshBasicMaterial color="#b9faff" transparent opacity={0.62} toneMapped={false} />
    </instancedMesh>
  )
}

function StartLine({ track }: TrackProps) {
  const sample = getTrackSample(track, 0)
  const heading = Math.atan2(sample.tangent.x, sample.tangent.y)
  return (
    <group position={[sample.position.x, 0.15, sample.position.y]} rotation={[0, heading, 0]}>
      {START_LINE_TILE_IDS.map((id, index) => (
        <mesh key={id} position={[-track.width + (index + 0.5) * ((track.width * 2) / 12), 0, 0]}>
          <boxGeometry args={[track.width / 6, 0.035, 1.25]} />
          <meshBasicMaterial color={index % 2 === 0 ? '#f7fbff' : '#151923'} toneMapped={false} />
        </mesh>
      ))}
    </group>
  )
}

function BrakingMarkers({ track }: TrackProps) {
  const markers = useMemo(() => {
    const hairpin = track.checkpoints[9] ?? Math.round(track.samples.length * 0.56)
    return [18, 12, 6].flatMap((lead, boardIndex) => {
      const sample = getTrackSample(track, hairpin - lead)
      return [-1, 1].map((side) => ({ sample, side, boardIndex }))
    })
  }, [track])
  return (
    <>
      {markers.map(({ sample, side, boardIndex }) => (
        <group
          key={`${sample.index}-${side}`}
          position={[
            sample.position.x + sample.normal.x * (track.width + 1.8) * side,
            1.25,
            sample.position.y + sample.normal.y * (track.width + 1.8) * side,
          ]}
          rotation={[0, Math.atan2(sample.tangent.x, sample.tangent.y), 0]}
        >
          <mesh>
            <boxGeometry args={[1.2, 2.5, 0.12]} />
            <meshStandardMaterial color="#eafcff" emissive="#ff315f" emissiveIntensity={1.4} roughness={0.35} />
          </mesh>
          {BRAKE_STRIPE_OFFSETS.slice(0, 3 - boardIndex).map((offset) => (
            <mesh key={offset} position={[offset, 0, side * 0.07]}>
              <boxGeometry args={[0.15, 1.7, 0.04]} />
              <meshBasicMaterial color="#07090e" />
            </mesh>
          ))}
        </group>
      ))}
    </>
  )
}

function RailLights({ track }: TrackProps) {
  const samples = useMemo(() => track.samples.filter((_, index) => index % 6 === 0), [track])
  return (
    <>
      {samples.flatMap((sample) =>
        [-1, 1].map((side) => (
          <group
            key={`${sample.index}-${side}`}
            position={[
              sample.position.x + sample.normal.x * (track.width + 2.4) * side,
              1.2,
              sample.position.y + sample.normal.y * (track.width + 2.4) * side,
            ]}
          >
            <mesh>
              <boxGeometry args={[0.12, 2.2, 0.12]} />
              <meshStandardMaterial color="#111724" metalness={0.8} roughness={0.25} />
            </mesh>
            <mesh position={[0, 1.12, 0]}>
              <sphereGeometry args={[0.16, 8, 8]} />
              <meshBasicMaterial color={side === 1 ? '#ff315f' : '#4df4ff'} toneMapped={false} />
            </mesh>
          </group>
        )),
      )}
    </>
  )
}

function CheckpointMarkers({ track }: TrackProps) {
  const markers = useMemo(() => createCheckpointMarkers(track), [track])
  return (
    <>
      {markers.map((marker) => (
        <group key={marker.key} position={[marker.position.x, 0, marker.position.y]} rotation={[0, marker.heading, 0]}>
          <mesh position={[0, marker.roadStripeY, 0]}>
            <boxGeometry args={[marker.roadStripeWidth, 0.025, marker.roadStripeDepth]} />
            <meshBasicMaterial color={marker.accent} transparent opacity={0.58} depthWrite={false} toneMapped={false} />
          </mesh>
          {([-1, 1] as const).map((side) => (
            <group key={side} position={[marker.beaconLateral * side, 0, 0]}>
              <mesh position={[0, marker.beaconHeight / 2 + 0.08, 0]} castShadow receiveShadow>
                <boxGeometry args={[0.5, marker.beaconHeight, 0.5]} />
                <meshStandardMaterial color="#080d16" metalness={0.82} roughness={0.32} />
              </mesh>
              <mesh position={[-side * 0.27, marker.beaconHeight * 0.58 + 0.08, 0]}>
                <boxGeometry args={[0.07, marker.beaconHeight * 0.56, 0.56]} />
                <meshBasicMaterial color={marker.accent} toneMapped={false} />
              </mesh>
              <mesh position={[0, marker.beaconHeight + 0.11, 0]}>
                <boxGeometry args={[0.62, 0.08, 0.62]} />
                <meshBasicMaterial color={marker.accent} toneMapped={false} />
              </mesh>
            </group>
          ))}
        </group>
      ))}
    </>
  )
}

function City({ track }: TrackProps) {
  const buildings = useMemo(() => createSkylineBuildings(track), [track])
  const ref = useRef<THREE.InstancedMesh>(null)
  useLayoutEffect(() => {
    const mesh = ref.current
    if (!mesh) return
    const dummy = new THREE.Object3D()
    const blue = new THREE.Color('#101a36')
    const violet = new THREE.Color('#24112d')
    buildings.forEach((building, index) => {
      dummy.position.set(building.position.x, building.height / 2 - 0.5, building.position.y)
      dummy.scale.set(building.width, building.height, building.width)
      dummy.rotation.y = building.rotation
      dummy.updateMatrix()
      mesh.setMatrixAt(index, dummy.matrix)
      mesh.setColorAt(index, index % 3 === 0 ? violet : blue)
    })
    mesh.instanceMatrix.needsUpdate = true
    if (mesh.instanceColor) mesh.instanceColor.needsUpdate = true
  }, [buildings])
  return (
    <instancedMesh ref={ref} args={[undefined, undefined, buildings.length]} receiveShadow>
      <boxGeometry />
      <meshStandardMaterial metalness={0.65} roughness={0.72} emissive="#070b18" emissiveIntensity={0.8} />
    </instancedMesh>
  )
}

export function Track({ track, runtime }: TrackProps) {
  const roadGeometry = useMemo(() => buildRoadGeometry(track), [track])
  return (
    <group>
      <mesh geometry={roadGeometry} receiveShadow position={[0, 0.06, 0]}>
        <meshPhysicalMaterial
          color="#0a0d14"
          roughness={0.28}
          metalness={0.22}
          clearcoat={0.92}
          clearcoatRoughness={0.08}
        />
      </mesh>
      <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, -0.08, 0]} receiveShadow>
        <circleGeometry args={[245, 96]} />
        <meshStandardMaterial color="#02040a" roughness={0.92} metalness={0.12} />
      </mesh>
      <gridHelper args={[480, 96, '#112342', '#091325']} position={[0, 0.02, 0]} />
      <Curbs track={track} />
      <BarrierRails track={track} runtime={runtime} />
      <CenterGuides track={track} />
      <StartLine track={track} />
      <BrakingMarkers track={track} />
      <RailLights track={track} />
      <CheckpointMarkers track={track} />
      <City track={track} />
    </group>
  )
}
